import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Users, 
  Unlock, 
  Lock,
  Check,
  Clock,
  CreditCard,
  Search,
  ChevronDown,
  ChevronUp,
  Plus,
  X,
  UserPlus,
  ToggleLeft,
  ToggleRight,
  Eye,
  Wrench,
  Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import api from '../../lib/api';

const AdminSpecialCourseEnrollments = () => {
  const { slug } = useParams();
  // Default to facilitadoras if no slug provided (legacy route support)
  const courseSlug = slug || 'facilitadoras-sosten-energetico';
  
  const [course, setCourse] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedEnrollment, setExpandedEnrollment] = useState(null);
  const [unlockingModule, setUnlockingModule] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingStudent, setAddingStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({ name: '', email: '', modality: 'online' });
  const [togglingAccess, setTogglingAccess] = useState(false);
  const [viewingStudent, setViewingStudent] = useState(null);
  const [studentView, setStudentView] = useState(null);
  const [fixingEnrollments, setFixingEnrollments] = useState(false);
  const [syncingEnrollment, setSyncingEnrollment] = useState(null);
  const [txDialogOpen, setTxDialogOpen] = useState(false);
  const [txDialogEnrollment, setTxDialogEnrollment] = useState(null);
  const [txForm, setTxForm] = useState({ amount: '', installment_number: '', paid_at: '' });

  useEffect(() => {
    fetchData();
  }, [courseSlug]);

  const fetchData = async () => {
    try {
      const courseRes = await api.get(`/special-courses/${courseSlug}`);
      setCourse(courseRes.data);
      
      // Get enrollments using course ID
      if (courseRes.data?.id) {
        const enrollRes = await api.get(`/admin/special-courses/${courseRes.data.id}/enrollments`);
        setEnrollments(enrollRes.data);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar el curso');
    } finally {
      setLoading(false);
    }
  };

  const [togglingModule, setTogglingModule] = useState(null);

  const handleToggleModuleGlobal = async (moduleId) => {
    if (!course?.id) return;
    setTogglingModule(moduleId);
    try {
      const res = await api.put(`/admin/special-courses/${course.id}/toggle-module-global`, { module_id: moduleId });
      toast.success(res.data.message);
      // Update local course state
      setCourse(prev => {
        const current = prev.globally_unlocked_modules || [];
        const updated = res.data.unlocked
          ? [...current, moduleId]
          : current.filter(id => id !== moduleId);
        return { ...prev, globally_unlocked_modules: updated };
      });
    } catch (error) {
      toast.error('Error al cambiar estado del módulo');
    } finally {
      setTogglingModule(null);
    }
  };

  const handleToggleModuleIndividual = async (enrollmentId, moduleId) => {
    setUnlockingModule(`${enrollmentId}-${moduleId}`);
    try {
      const res = await api.put(`/admin/special-enrollments/${enrollmentId}/unlock-module?module_id=${moduleId}`);
      toast.success(res.data.message);
      // Update local enrollment state
      setEnrollments(prev => prev.map(e => {
        if (e.id !== enrollmentId) return e;
        const blocked = e.blocked_modules || [];
        const updated = res.data.blocked
          ? [...blocked, moduleId]
          : blocked.filter(id => id !== moduleId);
        return { ...e, blocked_modules: updated };
      }));
    } catch (error) {
      toast.error('Error al cambiar acceso del módulo');
    } finally {
      setUnlockingModule(null);
    }
  };

  const handleToggleCourseAccess = async () => {
    if (!course?.id) return;
    setTogglingAccess(true);
    try {
      const res = await api.put(`/admin/special-courses/${course.id}/toggle-course-access`);
      toast.success(res.data.message);
      setCourse(prev => ({ ...prev, course_access_enabled: res.data.course_access_enabled }));
    } catch (error) {
      toast.error('Error al cambiar el acceso');
    } finally {
      setTogglingAccess(false);
    }
  };

  const handleViewAsStudent = async (userId) => {
    setViewingStudent(userId);
    try {
      const res = await api.get(`/admin/student/${userId}/view`);
      setStudentView(res.data);
    } catch (error) {
      toast.error('Error al cargar vista de alumna');
      setViewingStudent(null);
    }
  };

  const handleFixEnrollments = async () => {
    setFixingEnrollments(true);
    try {
      // First: migrate any legacy enrollments from `enrollments` table → `special_enrollments`
      let migratedMsg = '';
      if (course?.id) {
        try {
          const migRes = await api.post(`/admin/special-courses/${course.id}/repair-enrollments`);
          if (migRes.data?.migrated_enrollments > 0) {
            migratedMsg = `Migradas ${migRes.data.migrated_enrollments} inscripciones desde el sistema antiguo (con ${migRes.data.migrated_payments} pagos). `;
          }
        } catch (_) { /* ignore migration errors and continue */ }
      }
      // Then: legacy fix endpoint (course_id mismatches)
      const res = await api.post('/admin/fix-enrollment-course-ids');
      toast.success(migratedMsg + (res.data.message || 'Reparación completada'));
      fetchData();
    } catch (error) {
      toast.error('Error al reparar inscripciones');
    } finally {
      setFixingEnrollments(false);
    }
  };

  const handleSyncPaypal = async (enrollment) => {
    setSyncingEnrollment(enrollment.id);
    try {
      const res = await api.post(`/admin/special-enrollments/${enrollment.id}/sync-paypal`);
      const { inserted, transactions_in_paypal, installments_paid, installments_total } = res.data;
      if (inserted > 0) {
        toast.success(`Sincronizado: ${inserted} cuota(s) nueva(s) registrada(s). Total: ${installments_paid}/${installments_total}.`);
      } else {
        toast.info(`Ya estaba al día (${transactions_in_paypal} cuotas en PayPal, ${installments_paid}/${installments_total}).`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al sincronizar con PayPal');
    } finally {
      setSyncingEnrollment(null);
    }
  };

  const openAddTransactionDialog = (enrollment) => {
    const suggestedAmount = enrollment.installment_amount || (enrollment.installments_total > 0 ? Math.round((enrollment.total_price / enrollment.installments_total) * 100) / 100 : enrollment.total_price) || 0;
    const nextNum = (enrollment.installments_paid || 0) + 1;
    setTxDialogEnrollment(enrollment);
    setTxForm({
      amount: String(suggestedAmount),
      installment_number: String(nextNum),
      paid_at: new Date().toISOString().slice(0, 10),
    });
    setTxDialogOpen(true);
  };

  const handleSubmitTransaction = async (e) => {
    e?.preventDefault?.();
    if (!txDialogEnrollment) return;
    const amount = parseFloat(txForm.amount);
    if (!amount || amount <= 0) {
      toast.error('Importe inválido');
      return;
    }
    try {
      await api.post(`/admin/special-enrollments/${txDialogEnrollment.id}/transactions`, {
        amount,
        installment_number: txForm.installment_number ? parseInt(txForm.installment_number, 10) : undefined,
        paid_at: txForm.paid_at || undefined,
      });
      toast.success('Cuota registrada');
      setTxDialogOpen(false);
      setTxDialogEnrollment(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al añadir la cuota');
    }
  };

  const handleDeleteTransaction = async (enrollment, tx) => {
    if (!window.confirm(`¿Eliminar esta transacción de ${tx.amount}€?`)) return;
    try {
      await api.delete(`/admin/special-enrollments/${enrollment.id}/transactions/${tx.id}`);
      toast.success('Transacción eliminada');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };


  const handleDeleteEnrollment = async (enrollment) => {
    const studentLabel = enrollment.user?.name || enrollment.user?.email || 'esta alumna';
    const confirmed = window.confirm(
      `¿Eliminar la inscripción de ${studentLabel} en este curso?\n\n` +
      `Esto SOLO le quitará la matrícula a este curso (no se elimina su cuenta ni sus otros cursos).\n\n` +
      `Esta acción no se puede deshacer.`
    );
    if (!confirmed) return;
    try {
      await api.delete(`/admin/special-enrollments/${enrollment.id}`);
      toast.success(`Inscripción de ${studentLabel} eliminada`);
      setEnrollments(prev => prev.filter(e => e.id !== enrollment.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar la inscripción');
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    if (!newStudent.name || !newStudent.email) {
      toast.error('Completa todos los campos');
      return;
    }
    
    setAddingStudent(true);
    try {
      const res = await api.post(`/admin/special-courses/${course.id}/enroll-manual`, newStudent);
      const tempPassword = res.data?.temporary_password;
      if (tempPassword) {
        toast.success(`Alumno creado. Contraseña temporal: ${tempPassword}`);
      } else {
        toast.success(`Alumno ${newStudent.name} añadido correctamente`);
      }
      setShowAddModal(false);
      setNewStudent({ name: '', email: '', modality: 'online' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al añadir alumno');
    } finally {
      setAddingStudent(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      pending_deposit: 'bg-yellow-100 text-yellow-800',
      pending_subscription: 'bg-blue-100 text-blue-800',
      suspended: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      active: 'Activo',
      pending_deposit: 'Pendiente señal',
      pending_subscription: 'Pendiente suscripción',
      suspended: 'Suspendido',
      cancelled: 'Cancelado'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending_deposit}`}>
        {labels[status] || status}
      </span>
    );
  };

  const filteredEnrollments = enrollments.filter(e => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      e.user?.name?.toLowerCase().includes(search) ||
      e.user?.email?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[hsl(var(--sage-light))]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <Link 
          to="/admin/formaciones" 
          className="inline-flex items-center gap-2 text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Volver a formaciones
        </Link>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-heading text-[hsl(var(--text-primary))]">
              Gestión: {course?.title}
            </h1>
            <p className="text-[hsl(var(--text-secondary))]">
              {enrollments.length} inscripciones totales
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Global Course Access Control */}
            <div 
              className={`flex items-center gap-3 px-4 py-2 rounded-xl border ${
                course?.course_access_enabled 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-amber-50 border-amber-200'
              }`}
            >
              <div className="text-sm">
                <p className="font-medium text-[hsl(var(--text-primary))]">
                  {course?.course_access_enabled ? 'Curso abierto' : 'Curso cerrado'}
                </p>
                <p className="text-xs text-[hsl(var(--text-tertiary))]">
                  {course?.course_access_enabled 
                    ? 'Los alumnos pueden acceder' 
                    : 'Nadie puede acceder aún'}
                </p>
              </div>
              <button
                onClick={handleToggleCourseAccess}
                disabled={togglingAccess}
                className={`p-1 rounded-lg transition-colors ${
                  course?.course_access_enabled 
                    ? 'text-green-600 hover:bg-green-100' 
                    : 'text-amber-600 hover:bg-amber-100'
                }`}
                title={course?.course_access_enabled ? 'Cerrar acceso' : 'Abrir acceso'}
              >
                {togglingAccess ? (
                  <div className="w-8 h-8 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : course?.course_access_enabled ? (
                  <ToggleRight className="w-8 h-8" />
                ) : (
                  <ToggleLeft className="w-8 h-8" />
                )}
              </button>
            </div>
            
            <Button 
              onClick={() => setShowAddModal(true)}
              className="btn-sage"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Añadir alumno
            </Button>
            
            <Button 
              onClick={handleFixEnrollments}
              variant="outline"
              disabled={fixingEnrollments}
              className="text-sm"
              data-testid="fix-enrollments-btn"
            >
              <Wrench className="w-4 h-4 mr-2" />
              {fixingEnrollments ? 'Reparando...' : 'Reparar inscripciones'}
            </Button>
            
            <div className="relative w-full lg:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--text-tertiary))]" />
              <Input
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>

        {/* Add Student Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg">Añadir alumno manualmente</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <form onSubmit={handleAddStudent} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nombre completo</label>
                  <Input
                    value={newStudent.name}
                    onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
                    placeholder="María García"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <Input
                    type="email"
                    value={newStudent.email}
                    onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                    placeholder="maria@ejemplo.com"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Modalidad</label>
                  <select
                    value={newStudent.modality}
                    onChange={(e) => setNewStudent({ ...newStudent, modality: e.target.value })}
                    className="w-full border rounded-md p-2 text-sm"
                  >
                    <option value="online">Online</option>
                    <option value="presencial">Presencial</option>
                    <option value="pack_completo">Pack Completo</option>
                  </select>
                </div>
                
                <p className="text-xs text-gray-500">
                  Si el email no existe, se creará una cuenta con contraseña temporal automática.
                  El alumno tendrá acceso a todos los módulos.
                </p>
                
                <div className="flex gap-2 pt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowAddModal(false)}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 btn-sage"
                    disabled={addingStudent}
                  >
                    {addingStudent ? 'Añadiendo...' : 'Añadir alumno'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Global Module Control */}
        <div className="card-soft mb-8" data-testid="global-module-control">
          <h2 className="text-lg font-heading text-[hsl(var(--text-primary))] mb-2">
            Control global de módulos
          </h2>
          <p className="text-sm text-[hsl(var(--text-secondary))] mb-4">
            Abre o cierra módulos para <strong>todas</strong> las alumnas. Si cierras un módulo, se cierra para todas.
          </p>
          <div className="space-y-2">
            {(course?.student_area?.blocks || []).map((block, index) => {
              const isOpen = (course?.globally_unlocked_modules || []).includes(block.id);
              const isToggling = togglingModule === block.id;
              return (
                <div
                  key={block.id}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    isOpen
                      ? 'bg-green-50/50 border-green-200'
                      : 'bg-gray-50/50 border-gray-200'
                  }`}
                  data-testid={`global-module-${block.id}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                      isOpen ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'
                    }`}>{index + 1}</span>
                    <span className="text-sm text-[hsl(var(--text-primary))]">{block.title || `Módulo ${index + 1}`}</span>
                  </div>
                  <button
                    onClick={() => handleToggleModuleGlobal(block.id)}
                    disabled={isToggling}
                    className="flex items-center gap-2 transition-opacity"
                    data-testid={`toggle-global-${block.id}`}
                  >
                    {isToggling ? (
                      <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    ) : isOpen ? (
                      <ToggleRight className="w-8 h-8 text-green-500" />
                    ) : (
                      <ToggleLeft className="w-8 h-8 text-gray-400" />
                    )}
                    <span className={`text-xs font-medium ${isOpen ? 'text-green-600' : 'text-gray-400'}`}>
                      {isOpen ? 'Abierto' : 'Cerrado'}
                    </span>
                  </button>
                </div>
              );
            })}
            {(course?.student_area?.blocks || []).length === 0 && (
              <p className="text-sm text-gray-400 italic">Este curso no tiene esferas formativas registradas.</p>
            )}
          </div>
        </div>

        {/* Global Sphere Control — preliminary + practice spheres (Límites & similar) */}
        {(((course?.student_area?.preliminary_blocks || []).length > 0) || ((course?.student_area?.practice_blocks || []).length > 0)) && (
          <div className="card-soft mb-8" data-testid="global-sphere-control">
            <h2 className="text-lg font-heading text-[hsl(var(--text-primary))] mb-2">
              Control global de esferas
            </h2>
            <p className="text-sm text-[hsl(var(--text-secondary))] mb-4">
              Abre o cierra esferas introductorias y prácticas para <strong>todas</strong> las alumnas. La esfera de "Tus semillas de luz" siempre está abierta.
            </p>

            {(course?.student_area?.preliminary_blocks || []).length > 0 && (
              <div className="mb-5">
                <p className="text-xs uppercase tracking-wider text-[hsl(var(--text-tertiary))] mb-2">
                  {course?.student_area?.preliminary_blocks_section_title || 'Esferas introductorias'}
                </p>
                <div className="space-y-2">
                  {[...(course?.student_area?.preliminary_blocks || [])]
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((sphere, index) => {
                      const isOpen = (course?.globally_unlocked_modules || []).includes(sphere.id);
                      const isToggling = togglingModule === sphere.id;
                      return (
                        <div
                          key={sphere.id}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                            isOpen ? 'bg-green-50/50 border-green-200' : 'bg-gray-50/50 border-gray-200'
                          }`}
                          data-testid={`global-preliminary-${sphere.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                              isOpen ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'
                            }`}>{index + 1}</span>
                            <span className="text-sm text-[hsl(var(--text-primary))]">{sphere.title || `Esfera ${index + 1}`}</span>
                          </div>
                          <button
                            onClick={() => handleToggleModuleGlobal(sphere.id)}
                            disabled={isToggling}
                            className="flex items-center gap-2 transition-opacity"
                            data-testid={`toggle-preliminary-${sphere.id}`}
                          >
                            {isToggling ? (
                              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                            ) : isOpen ? (
                              <ToggleRight className="w-8 h-8 text-green-500" />
                            ) : (
                              <ToggleLeft className="w-8 h-8 text-gray-400" />
                            )}
                            <span className={`text-xs font-medium ${isOpen ? 'text-green-600' : 'text-gray-400'}`}>
                              {isOpen ? 'Abierta' : 'Cerrada'}
                            </span>
                          </button>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {(course?.student_area?.practice_blocks || []).length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-[hsl(var(--text-tertiary))] mb-2">
                  {course?.student_area?.practice_blocks_section_title || 'Esferas prácticas'}
                </p>
                <div className="space-y-2">
                  {[...(course?.student_area?.practice_blocks || [])]
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((sphere, index) => {
                      const isFavorites = !!sphere.is_favorites_block;
                      const isOpen = isFavorites || (course?.globally_unlocked_modules || []).includes(sphere.id);
                      const isToggling = togglingModule === sphere.id;
                      return (
                        <div
                          key={sphere.id}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                            isFavorites
                              ? 'bg-[#FBF8F1] border-[#D4A84B]/30'
                              : isOpen ? 'bg-green-50/50 border-green-200' : 'bg-gray-50/50 border-gray-200'
                          }`}
                          data-testid={`global-practice-${sphere.id}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${
                              isFavorites ? 'bg-[#D4A84B]/20 text-[#8a7428]' : isOpen ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'
                            }`}>{index + 1}</span>
                            <span className="text-sm text-[hsl(var(--text-primary))] truncate">{sphere.title || `Esfera ${index + 1}`}</span>
                            {isFavorites && (
                              <span className="ml-1 inline-flex items-center text-[10px] uppercase tracking-wider text-[#8a7428] bg-white border border-[#D4A84B]/30 px-2 py-0.5 rounded-full flex-shrink-0">
                                Tus semillas
                              </span>
                            )}
                          </div>
                          {isFavorites ? (
                            <span className="text-xs text-[#8a7428] font-medium flex items-center gap-1">
                              <ToggleRight className="w-6 h-6 text-[#D4A84B]" />
                              Siempre abierta
                            </span>
                          ) : (
                            <button
                              onClick={() => handleToggleModuleGlobal(sphere.id)}
                              disabled={isToggling}
                              className="flex items-center gap-2 transition-opacity"
                              data-testid={`toggle-practice-${sphere.id}`}
                            >
                              {isToggling ? (
                                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                              ) : isOpen ? (
                                <ToggleRight className="w-8 h-8 text-green-500" />
                              ) : (
                                <ToggleLeft className="w-8 h-8 text-gray-400" />
                              )}
                              <span className={`text-xs font-medium ${isOpen ? 'text-green-600' : 'text-gray-400'}`}>
                                {isOpen ? 'Abierta' : 'Cerrada'}
                              </span>
                            </button>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Enrollments List */}
        <div className="space-y-4">
          {filteredEnrollments.length === 0 ? (
            <div className="card-soft text-center py-12">
              <Users className="w-12 h-12 text-[hsl(var(--sage))] mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-[hsl(var(--text-secondary))]">
                {searchTerm ? 'No se encontraron estudiantes' : 'Aún no hay inscripciones'}
              </p>
            </div>
          ) : (
            filteredEnrollments.map((enrollment) => (
              <div key={enrollment.id} className="card-soft">
                {/* Header */}
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedEnrollment(
                    expandedEnrollment === enrollment.id ? null : enrollment.id
                  )}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[hsl(var(--sage-light))] flex items-center justify-center">
                      <span className="text-[hsl(var(--sage))] font-medium">
                        {enrollment.user?.name?.[0]?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-[hsl(var(--text-primary))]">
                        {enrollment.user?.name || 'Sin nombre'}
                      </p>
                      <p className="text-sm text-[hsl(var(--text-secondary))]">
                        {enrollment.user?.email}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right hidden sm:block">
                      <p className="text-sm text-[hsl(var(--text-tertiary))]">Modalidad</p>
                      <p className="font-medium text-[hsl(var(--text-primary))] capitalize">
                        {enrollment.modality}
                      </p>
                    </div>
                    {getStatusBadge(enrollment.status)}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleViewAsStudent(enrollment.user_id); }}
                      className="text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                      data-testid={`view-as-student-${enrollment.user_id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Ver como alumna
                    </Button>
                    {expandedEnrollment === enrollment.id ? (
                      <ChevronUp className="w-5 h-5 text-[hsl(var(--text-tertiary))]" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[hsl(var(--text-tertiary))]" />
                    )}
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedEnrollment === enrollment.id && (
                  <div className="mt-6 pt-6 border-t border-[hsl(var(--border))]">
                    {/* Stats — adapted to the actual payment options of each course */}
                    {(() => {
                      const totalPrice = enrollment.total_price || enrollment.total_amount || 0;
                      const installmentsTotal = enrollment.installments_total || 1;
                      const installmentsPaid = enrollment.installments_paid || 0;
                      const isInstallment = enrollment.payment_type === 'subscription' || enrollment.payment_type === 'installment' || installmentsTotal > 1;
                      const installmentAmount = enrollment.installment_amount || (installmentsTotal > 0 ? Math.round((totalPrice / installmentsTotal) * 100) / 100 : totalPrice);
                      const isFullyPaid = installmentsPaid >= installmentsTotal;
                      // Card 2: payment modality
                      const modalityLabel = isInstallment
                        ? `${installmentsTotal} cuotas de ${installmentAmount}€`
                        : 'Pago único';
                      // Card 3: payment progress
                      let progressLabel, progressColor;
                      if (!isInstallment) {
                        progressLabel = isFullyPaid ? 'Pagado' : 'Pendiente';
                        progressColor = isFullyPaid ? 'text-green-700' : 'text-amber-600';
                      } else {
                        progressLabel = `${installmentsPaid} / ${installmentsTotal}`;
                        progressColor = isFullyPaid ? 'text-green-700' : 'text-[hsl(var(--text-primary))]';
                      }
                      return (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                          <div className="p-4 rounded-xl bg-[hsl(var(--sage-light))]">
                            <div className="flex items-center gap-2 text-[hsl(var(--sage))] mb-1">
                              <CreditCard className="w-4 h-4" />
                              <span className="text-xs">Total</span>
                            </div>
                            <p className="font-heading text-lg text-[hsl(var(--text-primary))]" data-testid={`stat-total-${enrollment.id}`}>
                              {totalPrice}€
                            </p>
                          </div>
                          <div className="p-4 rounded-xl bg-[hsl(var(--sage-light))]">
                            <div className="flex items-center gap-2 text-[hsl(var(--sage))] mb-1">
                              <Check className="w-4 h-4" />
                              <span className="text-xs">Modalidad</span>
                            </div>
                            <p className="font-heading text-lg text-[hsl(var(--text-primary))]" data-testid={`stat-modality-${enrollment.id}`}>
                              {modalityLabel}
                            </p>
                          </div>
                          <div className="p-4 rounded-xl bg-[hsl(var(--sage-light))]">
                            <div className="flex items-center gap-2 text-[hsl(var(--sage))] mb-1">
                              <Clock className="w-4 h-4" />
                              <span className="text-xs">Pagos</span>
                            </div>
                            <p className={`font-heading text-lg ${progressColor}`} data-testid={`stat-payments-${enrollment.id}`}>
                              {progressLabel}
                            </p>
                          </div>
                          <div className="p-4 rounded-xl bg-[hsl(var(--sage-light))]">
                            <div className="flex items-center gap-2 text-[hsl(var(--sage))] mb-1">
                              <Unlock className="w-4 h-4" />
                              <span className="text-xs">Módulos</span>
                            </div>
                            <p className="font-heading text-lg text-[hsl(var(--text-primary))]">
                              {(() => {
                                const globalOpen = course?.globally_unlocked_modules || [];
                                const blocked = enrollment.blocked_modules || [];
                                const accessible = globalOpen.filter(id => !blocked.includes(id));
                                return `${accessible.length} / ${course?.student_area?.blocks?.length || 0}`;
                              })()}
                            </p>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Module Access Grid */}
                    <div>
                      <h3 className="text-sm font-medium text-[hsl(var(--text-primary))] mb-3">
                        Acceso a módulos
                      </h3>
                      <p className="text-xs text-[hsl(var(--text-tertiary))] mb-3">
                        Hereda del control global. Solo puedes bloquear/desbloquear individualmente módulos que estén abiertos globalmente.
                      </p>
                      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                        {(course?.student_area?.blocks || []).map((block, index) => {
                          const globallyOpen = (course?.globally_unlocked_modules || []).includes(block.id);
                          const individuallyBlocked = (enrollment.blocked_modules || []).includes(block.id);
                          const hasAccess = globallyOpen && !individuallyBlocked;
                          const isToggling = unlockingModule === `${enrollment.id}-${block.id}`;
                          
                          return (
                            <button
                              key={block.id}
                              onClick={() => globallyOpen && handleToggleModuleIndividual(enrollment.id, block.id)}
                              disabled={!globallyOpen || isToggling}
                              className={`rounded-lg p-2 flex flex-col items-center justify-center text-xs transition-all ${
                                !globallyOpen
                                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed opacity-50'
                                  : hasAccess
                                    ? 'bg-[hsl(var(--sage))] text-white hover:bg-[hsl(var(--sage))]/80'
                                    : 'bg-red-50 text-red-400 hover:bg-red-100 border border-red-200'
                              }`}
                              title={
                                !globallyOpen
                                  ? 'Cerrado globalmente'
                                  : hasAccess
                                    ? 'Click para bloquear individualmente'
                                    : 'Bloqueado individualmente — Click para desbloquear'
                              }
                              data-testid={`individual-module-${enrollment.id}-${block.id}`}
                            >
                              {isToggling ? (
                                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              ) : !globallyOpen ? (
                                <Lock className="w-4 h-4 mb-1" />
                              ) : hasAccess ? (
                                <Unlock className="w-4 h-4 mb-1" />
                              ) : (
                                <X className="w-4 h-4 mb-1" />
                              )}
                              <span className="truncate w-full text-center">{block.title?.substring(0, 10) || index + 1}</span>
                              {!globallyOpen && <span className="text-[9px] mt-0.5">Global</span>}
                              {globallyOpen && individuallyBlocked && <span className="text-[9px] mt-0.5">Bloqueado</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Transactions */}
                    {enrollment.transactions?.length > 0 && (
                      <div className="mt-6">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-medium text-[hsl(var(--text-primary))]">
                            Transacciones
                          </h3>
                          <div className="flex items-center gap-2">
                            {enrollment.paypal_subscription_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleSyncPaypal(enrollment); }}
                                disabled={syncingEnrollment === enrollment.id}
                                className="text-xs text-blue-700 border border-blue-200 hover:bg-blue-50"
                                data-testid={`sync-paypal-${enrollment.id}`}
                              >
                                {syncingEnrollment === enrollment.id ? 'Sincronizando…' : 'Sincronizar con PayPal'}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); openAddTransactionDialog(enrollment); }}
                              className="text-xs text-[hsl(var(--text-primary))] border border-[hsl(var(--border))] hover:bg-[hsl(var(--sage-light))]"
                              data-testid={`add-tx-${enrollment.id}`}
                            >
                              + Añadir cuota manual
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          {enrollment.transactions.map((tx) => (
                            <div 
                              key={tx.id}
                              className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--sage-light))] text-sm"
                            >
                              <div>
                                <span className="font-medium capitalize">
                                  {tx.transaction_type === 'single_payment'
                                    ? 'Pago único'
                                    : tx.installment_number
                                      ? `Cuota ${tx.installment_number}`
                                      : 'Pago'}
                                </span>
                                <span className="text-[hsl(var(--text-tertiary))] ml-2">
                                  {new Date(tx.created_at).toLocaleDateString('es-ES')}
                                </span>
                                {tx.transaction_type === 'manual_payment' && (
                                  <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">manual</span>
                                )}
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="font-medium">{tx.amount}€</span>
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  tx.status === 'completed' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {tx.status === 'completed' ? 'Completado' : 'Pendiente'}
                                </span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDeleteTransaction(enrollment, tx); }}
                                  className="text-red-600 hover:text-red-800 text-xs"
                                  title="Eliminar transacción"
                                  data-testid={`delete-tx-${tx.id}`}
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* If no transactions yet, still show actions to add */}
                    {(!enrollment.transactions || enrollment.transactions.length === 0) && (
                      <div className="mt-6">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-medium text-[hsl(var(--text-primary))]">Transacciones</h3>
                          <div className="flex items-center gap-2">
                            {enrollment.paypal_subscription_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleSyncPaypal(enrollment); }}
                                disabled={syncingEnrollment === enrollment.id}
                                className="text-xs text-blue-700 border border-blue-200 hover:bg-blue-50"
                                data-testid={`sync-paypal-${enrollment.id}`}
                              >
                                {syncingEnrollment === enrollment.id ? 'Sincronizando…' : 'Sincronizar con PayPal'}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); openAddTransactionDialog(enrollment); }}
                              className="text-xs text-[hsl(var(--text-primary))] border border-[hsl(var(--border))] hover:bg-[hsl(var(--sage-light))]"
                              data-testid={`add-tx-${enrollment.id}`}
                            >
                              + Añadir cuota manual
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-[hsl(var(--text-tertiary))] italic">Sin transacciones registradas todavía.</p>
                      </div>
                    )}

                    {/* Danger zone — delete enrollment */}
                    <div className="mt-8 pt-6 border-t border-red-200">
                      <div className="rounded-xl border border-red-200 bg-red-50/40 p-4">
                        <div className="flex items-start gap-3 mb-3">
                          <Trash2 className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-red-700">Zona peligrosa</p>
                            <p className="text-xs text-red-600/80 mt-0.5">
                              Quitar a esta alumna de este curso. <strong>No se elimina la cuenta de usuaria</strong> ni sus matrículas en otros cursos.
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDeleteEnrollment(enrollment); }}
                          className="text-xs text-red-700 border border-red-300 hover:bg-red-100 hover:text-red-800"
                          data-testid={`delete-enrollment-${enrollment.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Eliminar inscripción de este curso
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Manual Transaction Dialog */}
        {txDialogOpen && txDialogEnrollment && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => { setTxDialogOpen(false); setTxDialogEnrollment(null); }}
          >
            <div className="bg-white rounded-xl max-w-md w-full p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg">Añadir cuota manual</h3>
                <Button variant="ghost" size="sm" onClick={() => { setTxDialogOpen(false); setTxDialogEnrollment(null); }}>×</Button>
              </div>
              <p className="text-xs text-[hsl(var(--text-tertiary))] mb-4">
                Para {txDialogEnrollment.user?.name || txDialogEnrollment.user?.email}. Esto registra el pago en el sistema y dispara la creación de la factura.
              </p>
              <form onSubmit={handleSubmitTransaction} className="space-y-4">
                <div>
                  <label className="block text-xs text-[hsl(var(--text-tertiary))] mb-1">Importe (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={txForm.amount}
                    onChange={(e) => setTxForm(f => ({ ...f, amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm"
                    data-testid="manual-tx-amount"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[hsl(var(--text-tertiary))] mb-1">Número de cuota</label>
                  <input
                    type="number"
                    min="1"
                    value={txForm.installment_number}
                    onChange={(e) => setTxForm(f => ({ ...f, installment_number: e.target.value }))}
                    className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm"
                    data-testid="manual-tx-installment"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[hsl(var(--text-tertiary))] mb-1">Fecha del pago</label>
                  <input
                    type="date"
                    value={txForm.paid_at}
                    onChange={(e) => setTxForm(f => ({ ...f, paid_at: e.target.value }))}
                    className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm"
                    data-testid="manual-tx-date"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="ghost" type="button" onClick={() => { setTxDialogOpen(false); setTxDialogEnrollment(null); }}>Cancelar</Button>
                  <Button type="submit" className="bg-[hsl(var(--accent))] text-white" data-testid="manual-tx-submit">Registrar cuota</Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View As Student Modal */}
        {/* View As Student Modal */}
        {viewingStudent && studentView && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setViewingStudent(null); setStudentView(null); }}>
            <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-heading text-lg">Vista de alumna</h3>
                  <p className="text-sm text-gray-500">Esto es lo que ve {studentView.student?.name}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setViewingStudent(null); setStudentView(null); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium">{studentView.student?.name}</p>
                <p className="text-xs text-gray-500">{studentView.student?.email}</p>
              </div>

              {studentView.can_access_courses?.length > 0 ? (
                <div className="space-y-4">
                  {studentView.can_access_courses.map((ca, i) => (
                    <div key={i} className={`p-4 rounded-xl border ${ca.can_enter ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-sm">{ca.course_title}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${ca.can_enter ? 'bg-green-200 text-green-800' : 'bg-amber-200 text-amber-800'}`}>
                          {ca.can_enter ? 'Puede acceder' : 'Sin acceso'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                        <div>Estado: <span className="font-medium">{ca.status}</span></div>
                        <div>Curso abierto: <span className="font-medium">{ca.course_access_enabled ? 'Sí' : 'No'}</span></div>
                        <div>Módulos: <span className="font-medium">{ca.unlocked_modules}</span></div>
                        <div>Pago: <span className="font-medium capitalize">{ca.payment_type}</span></div>
                        <div>Cuotas pagadas: <span className="font-medium">{ca.installments_paid}</span></div>
                      </div>
                      {!ca.can_enter && (
                        <p className="text-xs text-amber-700 mt-2 italic">
                          {!ca.course_access_enabled 
                            ? 'La alumna ve: "El curso comenzará pronto. Te avisaremos cuando esté disponible."'
                            : ca.status !== 'active' 
                              ? 'La inscripción no está activa.'
                              : 'Sin acceso por otro motivo.'}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">Esta alumna no tiene inscripciones.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSpecialCourseEnrollments;
